import { useEffect, useCallback } from 'react';

/**
 * Custom hook to show a confirmation dialog when the user tries to:
 * 1. Close the browser tab
 * 2. Refresh the page
 * 3. Navigate away using browser back/forward buttons
 * 
 * @param {boolean} shouldBlock - Whether to show the confirmation dialog
 * @param {string} message - The message to show (only works for beforeunload, not custom dialogs)
 */
const useExitConfirmation = (shouldBlock = true, message = 'You have unsaved changes. Are you sure you want to leave?') => {
  
  // Handle browser beforeunload event (tab close, refresh, external navigation)
  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      // Modern browsers require returnValue to be set
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, message]);

  // Handle browser popstate event (back/forward buttons)
  useEffect(() => {
    if (!shouldBlock) return;

    // Push a dummy state to history so we can intercept back button
    window.history.pushState(null, '', window.location.pathname);

    const handlePopState = (e) => {
      // Show confirmation dialog
      const confirmed = window.confirm(message);
      if (!confirmed) {
        // User cancelled - push state again to prevent navigation
        window.history.pushState(null, '', window.location.pathname);
      }
      // If confirmed, the browser will naturally navigate back
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [shouldBlock, message]);

  // Function to manually confirm before programmatic navigation
  const confirmNavigation = useCallback((navigateFn) => {
    if (!shouldBlock) {
      navigateFn();
      return;
    }
    
    const confirmed = window.confirm(message);
    if (confirmed) {
      navigateFn();
    }
  }, [shouldBlock, message]);

  return { confirmNavigation };
};

export default useExitConfirmation;
